<script setup lang="ts">
import {defineEmits, onMounted, ref} from "vue";
import {useStore} from "../store";
import RateStar from "./RateStar.vue";
import IconClose from "./IconClose.vue";
import {useUserStore} from "../store/user";
import {useContentStore} from "../store/content";

const store = useStore()
const contentItem = useContentStore()
const userStore = useUserStore()
const emit = defineEmits(["toggle"])
const btnDisabled = ref<boolean>(true);
const rating = ref(0)
const completed = ref(false)
const loaded = ref(false)

function handleChoice(choice: number): void {
    rating.value = choice
    if (choice !== 0){
        btnDisabled.value = false
    }
}

function onSubmit(){
    btnDisabled.value = true
    completed.value = true
    setTimeout(() => emit('toggle'), 1000)

    store.rateItem(rating.value)
        .then(() => {
            userStore.updateItemRating(contentItem.itemId, rating.value)
        })
}

onMounted(() => {
    userStore.getItemRating(contentItem.itemId)
        .then((data) => {
            loaded.value = true
            rating.value = data
            if (data > 0){
                btnDisabled.value = false
            }
        })
})

function onClose(){
    emit('toggle')
}
</script>

<template>
    <div class="fixed inset-0 z-[999] grid h-screen w-screen place-items-center">
        <div v-if="!completed" class="bg-mono-ns-100 rounded-3xl w-[295px] xl:w-[569px] flex flex-col p-2 xl:p-4 font-sans gap-6">
            <button type="button" class="p-2.5 self-end text-mono-ps-200" @click="onClose">
                <IconClose class-name="w-6 h-6"></IconClose>
            </button>
            <h3 class="text-h3m xl:text-h3 font-bold text-mono-white text-center px-2 xl:px-4 -mt-4">Rate This Show</h3>

            <div v-if="loaded" class="flex flex-row justify-center items-center gap-2 text-mono-ns-200 px-2 xl:px-4">
                <RateStar :rating="1" :full="rating >= 1" @choice="handleChoice"></RateStar>
                <RateStar :rating="2" :full="rating >= 2" @choice="handleChoice"></RateStar>
                <RateStar :rating="3" :full="rating >= 3" @choice="handleChoice"></RateStar>
                <RateStar :rating="4" :full="rating >= 4" @choice="handleChoice"></RateStar>
                <RateStar :rating="5" :full="rating >= 5" @choice="handleChoice"></RateStar>
            </div>
            <div v-else class='flex space-x-2 justify-center items-center px-2 xl:px-4'>
                <span class='sr-only'>Loading...</span>
                <div class='h-8 w-8 bg-mono-ns-200 rounded-full animate-bounce [animation-delay:-0.3s]'></div>
                <div class='h-8 w-8 bg-mono-ns-200 rounded-full animate-bounce [animation-delay:-0.15s]'></div>
                <div class='h-8 w-8 bg-mono-ns-200 rounded-full animate-bounce'></div>
            </div>
            <div class="flex flex-col px-2 xl:px-4">
            <button class="mb-8 xl:mb-6 font-bold text-center btn rounded-2.4xl xl:rounded-3xl text-mono-ns-200 min-w-fit xl:w-52 w-full relative self-center"
                    :class="{
                        'border-2': btnDisabled,
                        'border-mono-ns-200': btnDisabled,
                        'bg-mono-white': !btnDisabled,
                        'hover:bg-mono-ps-100': !btnDisabled,
                        'cursor-not-allowed': btnDisabled,
                        'cursor-pointer': !btnDisabled,
                    }"
                    :disabled="btnDisabled" @click="onSubmit">Rate</button>
            </div>
        </div>
        <div v-else class="bg-mono-ns-100 rounded-3xl flex flex-col p-6 xl:p-10 font-sans items-center gap-4">
            <div class="rounded-full p-3 bg-sa-light text-mono-ns-100">
                <svg class="w-6 h-6" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6.5L9 17.5L4 12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
            </div>
            <h3>Thanks!</h3>
        </div>
    </div>
</template>
